import React, {useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import {withAuth} from "../../hoc/Auth";
import {Wrapper, Container, Column} from "../../hoc/Layout";
import Api from "../../Api";
import Search from "../../Components/Utils/SearchTags";
import DateRange from "../../Components/Utils/DateRange";
import TransactionsFilters from "../../Components/TransactionsFilters";
import TransactionsTable from "../../Components/TransactionsTable";
import ReportWidget from "../../Components/Utils/ReportWidget";
import Pagination from "react-js-pagination";
import {MultipleSelect} from "../../Components/Utils/FieldsHelper";
import ResponsiveSidebarFilters from "../../Components/Utils/ResponsiveSidebarFilters";

import HTabBar from "../../Components/Utils/HTabBar";
import TabBarItem from "../../Components/Utils/HTabBar/TabBarItem";

import MainChart from "../../Components/Charts/MainTransactionsChart";
import LineChart from "../../Components/Charts/TransactionsLineChart";
import MerchantsTurnoverChart from "../../Components/ComponentsOfAdministrator/Charts/MerchantsTurnoverChart";
import EmptyChart from "./EmptyChart";
import EmptyCard from "../../hoc/EmptyCard";

import classes from "./styles.module.css";

const LABELS = {
    purchase: "Purchase",
    purchaseDecline: "Purchase Decline",
    payout: "Payout",
    payoutDecline: "Payout Decline",
    payoutCreated: "Payout Created",
    sbp: "SBP",
    mP2P: "P2P",
    refund: "Refund",
    reversal: "Reversal",
    recurrent: "Recurrent",
    threeDSPending: "3DS Pending",
    purchasePending: "Purchase Pending",
    payoutPending: "Payout Pending",
    refundPending: "Refund Pending",
    reversalPending: "Reversal Pending",
    mP2PPending: "P2P Pending",
    sbpPending: "SBP Pending",
    chargeback: "Chargeback",
    created: "Created",
};

const COLORS = {
    purchase: "#3366CC",
    purchaseDecline: "#FF9900",
    payout: "#109618",
    payoutDecline: "#DC3912",
    payoutCreated: "#8B0000",
    sbp: "#00BFFF",
    mP2P: "#8A2BE2",
    refund: "#FF1493",
    reversal: "#808080",
    recurrent: "#20B2AA",
    threeDSPending: "#708090",
    purchasePending: "#778899",
    payoutPending: "#A9A9A9",
    refundPending: "#B0C4DE",
    reversalPending: "#A0522D",
    mP2PPending: "#483D8B",
    sbpPending: "#4682B4",
    chargeback: "#FFD700",
    created: "#6A5ACD",
    turnover: "#990099",
};

const USER_ALLOWED = ["payout", "payoutDecline", "payoutCreated"];

const STATISTICS_ACCESS = 4;

function checkAccess(accountAccessSettings, globalPrivileges, bitMask) {
    if (globalPrivileges !== null && globalPrivileges !== undefined) {
        return (globalPrivileges & bitMask) > 0;
    }
    if (Array.isArray(accountAccessSettings)) {
        return accountAccessSettings.some(el => (el.privileges & bitMask) > 0);
    }
    return false;
}

const Transactions = withAuth(({token, adminPrivileges, accountAccessSettings, globalPrivileges}) => {
    const mountedRef = useRef(true);
    const initialTagsRef = useRef([]);
    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const routFilters = useParams().filters;
    const searchBy = [
        {key: "id", locale: "Search.label.operationId", placeholder: "Search.example.id"},
        {key: "description", locale: "Search.label.description", placeholder: "Search.example.description"},
        {key: "rrn", locale: "Search.label.rrn", placeholder: "Search.example.rrn"},
        {
            key: "externalReference",
            locale: "Search.label.externalReference",
            placeholder: "Search.example.externalReference"
        },
        {key: "cardNumber", locale: "Search.label.cardNumber", placeholder: "Search.example.cardNumber"},
        {key: "cardHolder", locale: "Search.label.cardHolder", placeholder: "Search.example.cardHolder"},
        {key: "phone", locale: "Search.label.phone", placeholder: "Search.example.phone"},
        {key: "amountEqually", locale: "Search.label.amountEqually", placeholder: "Search.example.amount"},
        {key: "amountMore", locale: "Search.label.amountMore", placeholder: "Search.example.amount"},
        {key: "amountLess", locale: "Search.label.amountLess", placeholder: "Search.example.amountLess"},
    ];

    const [defaultPeriod, setDefaultPeriod] = useState("week");

    const [projects, setProjects] = useState(null);
    const [terminals, setTerminals] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [postsPerPage, setPostsPerPage] = useState(10);

    const [transactionsCount, setTransactionsCount] = useState(0);
    const [turnoverAmount, setTurnoverAmount] = useState(0);

    const [filterProjects, setFilterProjects] = useState([]);
    const [filterTerminals, setFilterTerminals] = useState([]);
    const [filterDate, setFilterDate] = useState({});
    const [filterBpaDate, setFilterBpaDate] = useState({});
    const [filters, setFilters] = useState([]);
    const [filtersTags, setFiltersTags] = useState([]);

    const [chartsData, setChartsData] = useState(null);

    const [mainChartData, setMainChartData] = useState(null);
    const [turnoverChartData, setTurnoverChartData] = useState(null);
    const [merchantsTurnoverData, setMerchantsTurnoverData] = useState(null);

    const [transactionsData, setTransactionsData] = useState([]);
    const [counts, setCounts] = useState({});

    const [isLoadingProjects, setIsLoadingProjects] = useState(true);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [loadingCharts, setLoadingCharts] = useState(false);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    // Load projects and initial filters
    useEffect(() => {
        new Api({token}).getProjects().then((r) => {
            if (!mountedRef.current) return;
            setProjects(r);
            setIsLoadingProjects(false);
        });

        if (routFilters) {
            const parseRouteFilters = JSON.parse(routFilters);

            if (parseRouteFilters.filters) {
                setFiltersTags(parseRouteFilters.filters);
                initialTagsRef.current = parseRouteFilters.filters;
                setDefaultPeriod("month");
            }

            if (parseRouteFilters.period) {
                setDefaultPeriod(parseRouteFilters.period);

                var startDate = null;
                var endDate = null;

                var options = {
                    day: "numeric",
                    month: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                };
                options.timeZoneName = "short";

                switch (parseRouteFilters.period) {
                    case "today":
                        startDate = new Date();
                        startDate.setHours(0, 0);
                        endDate = new Date();
                        break;
                    case "month":
                        startDate = new Date();
                        startDate.setDate(new Date().getDate() - 30);
                        startDate.setHours(0, 0);
                        endDate = new Date();
                        break;
                    default:
                        break;
                }

                var period = {
                    startDate: String(startDate.toLocaleDateString("ru", options)),
                    endDate: String(endDate.toLocaleDateString("ru", options)),
                };

                applyFilters(null, null, period);
                return;
            }
        }

        applyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Build terminals list from selected projects
    useEffect(() => {
        if (!projects || !Array.isArray(filterProjects) || filterProjects.length === 0) {
            setTerminals(null);
            setFilterTerminals([]);
            return;
        }

        const terminalsAcc = [];
        filterProjects.forEach((projectUUID) => {
            const project = projects.find((el) => el.uuid === projectUUID);
            if (project && project.providers) {
                project.providers.forEach((p) => terminalsAcc.push(p));
            }
        });

        // сохраняем только те терминалы, которые реально принадлежат выбранным проектам
        const cleanFilterTerminals = filterTerminals.filter((uuid) =>
            terminalsAcc.some((el) => el.uuid === uuid)
        );

        setFilterTerminals(cleanFilterTerminals);
        setTerminals(terminalsAcc);
    }, [filterProjects, projects]); // добавляем projects в зависимости

    function selectFilter(selectedArray) {
        setFilters(selectedArray);
    }

    function buildFiltersPayload(dateRangeOverride, tagsOverride) {
        var tags = tagsOverride || filtersTags;
        if (tags.length === 0 && initialTagsRef.current.length > 0) {
            tags = initialTagsRef.current;
            initialTagsRef.current = [];
        }
        const base = {
            filters: [...filters, ...tags],
            dateRange: dateRangeOverride ? dateRangeOverride : filterDate,
            page: currentPage,
            itemsPerPage: postsPerPage,
        };

        if (filterBpaDate && (filterBpaDate.startDate || filterBpaDate.endDate)) {
            base.balanceProcessedAtRange = filterBpaDate;
        }

        if (Array.isArray(filterProjects) && filterProjects.length > 0) {
            base.projects = filterProjects;
        }
        if (Array.isArray(filterTerminals) && filterTerminals.length > 0) {
            base.providers = filterTerminals;
        }

        return base;
    }

    function applyFilters(page, itemsPerPage, dateRange, tagsOverride) {
        setLoadingTransactions(false);

        const nextPage = page || 1;
        const nextItems = itemsPerPage || postsPerPage;

        setCurrentPage(nextPage);
        setPostsPerPage(nextItems);

        const payload = {
            ...buildFiltersPayload(dateRange, tagsOverride),
            page: nextPage,
            itemsPerPage: nextItems,
        };

        // загрузка таблицы
        new Api({token}).setTransactionsFilters(payload).then((r) => {
            if (!mountedRef.current) return;

            setTransactionsData(r && r.data ? r.data : []);

            if (r && r.count && r.count.total) {
                setTransactionsCount(Number(r.count.total.count));
                const t = Number(r.count.total.turnover || 0);
                setTurnoverAmount(Math.round((t + Number.EPSILON) * 100) / 100);
            } else {
                setTransactionsCount(0);
                setTurnoverAmount(0);
            }

            const nextCounts = {};
            if (r && r.count) {
                Object.keys(r.count).forEach((k) => {
                    if (k !== "total") {
                        const v = r.count[k];
                        nextCounts[k] = Number(v && v.count ? v.count : 0);
                    }
                });
            }

            setCounts(nextCounts);
            setLoadingTransactions(true);
        });

// загрузка графиков
        new Api({token}).setTransactionsChartFilters(payload).then((r) => {
            if (!mountedRef.current) return;

            setChartsData(r || null);
            setMainChartData(r && r.main ? r.main : null);
            setTurnoverChartData(r && r.turnover ? r.turnover : null);

            const t =
                r && r.turnover && typeof r.turnover.total !== "undefined"
                    ? Number(r.turnover.total)
                    : 0;

            setTurnoverAmount(Math.round((t + Number.EPSILON) * 100) / 100);

            setLoadingCharts(true);
        });

        // админ-виджет: оборот по мерчантам (только админам)
        if (adminPrivileges) {
            new Api({token}).setMerchantsTurnoverChart(payload).then((r) => {
                if (!mountedRef.current) return;
                setMerchantsTurnoverData(r || null);
            }).catch(() => {
                if (!mountedRef.current) return;
                setMerchantsTurnoverData({rows: [], total: {amount: 0, count: 0}});
            });
        }

    }

    function setPage(page, itemsPerPage) {
        const newPage = page || 1;
        const newItems = itemsPerPage || postsPerPage;

        setCurrentPage(newPage);
        setPostsPerPage(newItems);

        const payload = {
            ...buildFiltersPayload(),
            page: newPage,
            itemsPerPage: newItems,
        };

        new Api({token}).setTransactionsFilters(payload).then((r) => {
            if (!mountedRef.current) return;

            setTransactionsData(r && r.data ? r.data : []);
            setTransactionsCount(Number(r && r.count && r.count.total ? r.count.total.count : 0));

            const nextCounts = {};
            if (r && r.count) {
                Object.keys(r.count).forEach((k) => {
                    if (k !== "total") {
                        const v = r.count[k];
                        nextCounts[k] = Number(v && v.count ? v.count : 0);
                    }
                });
            }

            setCounts(nextCounts);
        });

    }

    function onSelectProjects(data) {
        setFilterProjects(data);
    }

    function onSelectTerminals(data) {
        setFilterTerminals(data);
    }

    function onChangeSearch(tags) {
        setFiltersTags(tags);
    }

    function visibleStatusKeys(cData) {
        if (!cData) return [];
        const keys = Object.keys(cData).filter(function (k) {
            return k !== "main" && k !== "turnover";
        });
        if (adminPrivileges) return keys;
        return keys.filter(function (k) {
            return USER_ALLOWED.indexOf(k) !== -1;
        });
    }

    if (!checkAccess(accountAccessSettings, globalPrivileges, STATISTICS_ACCESS)) {
        return (
            <Wrapper>
                <Container style={{justifyContent: "center"}}>
                    <Column width={1180}>
                        <div className="d-flex justify-content-center align-items-center" style={{minHeight: '60vh'}}>
                            <div className="text-center">
                                <h4>Нет доступа</h4>
                                <p className="text-muted">У вас нет прав для просмотра этого раздела</p>
                            </div>
                        </div>
                    </Column>
                </Container>
            </Wrapper>
        );
    }

    const filtersContent = (
        <div>
            <EmptyCard>
                <MultipleSelect
                    label={"FieldsHelper.label.project"}
                    placeholder={"FieldsHelper.label.selectProject"}
                    values={projects}
                    selectedValues={filterProjects}
                    isLoading={isLoadingProjects}
                    onChange={onSelectProjects}
                    excludeSearchColumn={[]}
                />

                <MultipleSelect
                    label={"FieldsHelper.label.terminal"}
                    placeholder={"FieldsHelper.label.selectTerminal"}
                    values={terminals}
                    selectedValues={filterTerminals}
                    isLoading={isLoadingProjects}
                    onChange={onSelectTerminals}
                    excludeSearchColumn={[]}
                />
            </EmptyCard>

            <EmptyCard>
                <DateRange onSelectDate={(range) => setFilterDate(range)} defaultPeriod={defaultPeriod}/>
            </EmptyCard>

            <EmptyCard>
                <div style={{fontSize: ".8125rem", fontWeight: 600, marginBottom: 4}}>Дата завершения</div>
                <DateRange onSelectDate={(range) => setFilterBpaDate(range)} defaultPeriod={null}/>
            </EmptyCard>

            <TransactionsFilters
                token={token}
                adminPrivileges={adminPrivileges}
                projects={filterProjects}
                onApplyFilters={applyFilters}
                onSelectFilter={selectFilter}
                isLoading={loadingTransactions}
            />

            <ReportWidget
                access={true}
                token={token}
                type="transactions"
                filter={buildFiltersPayload()}
                count={transactionsCount}
                countLabel={"Операций"}
                date={filterDate}
                name={"transactions_report"}
            />

            <ReportWidget
                access={["STATISTICS_REPORT_1C_VIEW"]}
                token={token}
                type="transactions1C"
                filter={buildFiltersPayload()}
                count={transactionsCount}
                countLabel={"Операций"}
                date={filterDate}
                name={"transactions_1Creport"}
            />
        </div>
    );

    return (
        <Wrapper>
            <Container>
                <Column width="780" className={classes.MainColumn}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>Платежи</h4>
                    </div>
                    <button
                        type="button"
                        className={classes.FiltersToggle}
                        onClick={() => setIsFiltersOpen(true)}
                    >
                        Фильтры
                    </button>
                    <div className={classes.Card}>
                        {loadingCharts ? (
                            <HTabBar>
                                <TabBarItem
                                    label="Общие данные"
                                    count={transactionsCount ? transactionsCount + " шт." : "0 шт"}
                                    isVisible={true}
                                >
                                    <MainChart data={mainChartData} count={transactionsCount}/>
                                </TabBarItem>

                                {chartsData &&
                                    visibleStatusKeys(chartsData).map(function (key) {
                                        const chart = chartsData[key];
                                        const label = LABELS[key] || key;
                                        const color = COLORS[key] || "#000000";
                                        const cntFromCounts =
                                            typeof counts[key] !== "undefined"
                                                ? counts[key]
                                                : chart && chart.count
                                                    ? chart.count
                                                    : 0;

                                        // ВАЖНО: не возвращаем null — отдаём скрытый TabBarItem
                                        if (!adminPrivileges && !cntFromCounts) {
                                            return (
                                                <TabBarItem
                                                    key={key}
                                                    label={label}
                                                    labelColor={color}
                                                    count={"0 шт."}
                                                    isVisible={false}
                                                >
                                                    <div/>
                                                </TabBarItem>
                                            );
                                        }

                                        return (
                                            <TabBarItem
                                                key={key}
                                                label={label}
                                                labelColor={color}
                                                count={cntFromCounts + " шт."}
                                                isVisible={true}
                                            >
                                                <LineChart
                                                    data={chart}
                                                    vAxisLabel={label + ", шт"}
                                                    lineColor={color}
                                                    count={cntFromCounts}
                                                />
                                            </TabBarItem>
                                        );
                                    })}

                                <TabBarItem
                                    label="Оборот"
                                    labelColor={COLORS.turnover}
                                    count={turnoverAmount ? turnoverAmount.toLocaleString() + " ₽" : "0 ₽"}
                                    isVisible={true}
                                >
                                    <LineChart
                                        data={turnoverChartData}
                                        vAxisLabel={"Оборот"}
                                        lineColor={COLORS.turnover}
                                        count={turnoverAmount}
                                    />
                                </TabBarItem>
                            </HTabBar>
                        ) : (
                            <EmptyChart/>
                        )}
                    </div>

                    {adminPrivileges && (
                        <div className={classes.Card}>
                            <div style={{padding: "12px 16px 4px 16px", fontWeight: 600, fontSize: 15}}>
                                Оборот по мерчантам
                            </div>
                            <MerchantsTurnoverChart data={merchantsTurnoverData}/>
                        </div>
                    )}

                    <Search
                        onChangeSearch={onChangeSearch}
                        onSearch={function(tags) { applyFilters(null, null, null, tags); }}
                        isDisabled={false}
                        defaultSearch={searchBy[0]}
                        searchBy={searchBy}
                    />

                    <TransactionsTable
                        token={token}
                        adminPrivileges={adminPrivileges}
                        transactions={transactionsData}
                        isLoading={loadingTransactions}
                    />

                    <div className={classes.PaginationWrap}>
                        <Pagination
                            firstPageText="⇤"
                            prevPageText="⇠"
                            lastPageText="⇥"
                            nextPageText="⇢"
                            activeLinkClass={classes.ActiveLink}
                            activePage={currentPage}
                            itemsCountPerPage={postsPerPage}
                            totalItemsCount={transactionsCount}
                            pageRangeDisplayed={10}
                            onChange={(page) => setPage(page, postsPerPage)}
                        />
                    </div>
                </Column>

                <Column width="350" className={classes.SidebarColumn}>
                    <ResponsiveSidebarFilters
                        title="Фильтры"
                        isOpen={isFiltersOpen}
                        onClose={() => setIsFiltersOpen(false)}
                    >
                        {filtersContent}
                    </ResponsiveSidebarFilters>
                </Column>
            </Container>
        </Wrapper>
    );
});

export default Transactions;



// WEBPACK FOOTER //
// ./src/PagesOfUser/Transactions/index.js